import { useState, useCallback } from 'react';
import { battlesApi } from '../../../api/battles';

/**
 * Вычислить первый свободный номер раунда.
 * Учитывает уже существующие номера (в т.ч. с "дырами" после удаления раундов).
 * @param {Array} rounds - Список существующих раундов
 * @returns {number} Первый незанятый номер (минимум 1)
 */
const getFirstFreeRoundNumber = (rounds = []) => {
  const takenNumbers = new Set(
    rounds.map((r) => parseInt(r.number, 10)).filter((n) => !isNaN(n) && n > 0)
  );
  let candidate = 1;
  while (takenNumbers.has(candidate)) {
    candidate += 1;
  }
  return candidate;
};

/** Пустая форма раунда */
const emptyRoundForm = (rounds = [], type = 'QUALIFYING') => ({
  title: '',
  description: '',
  theme: '',
  task: '',
  type,
  status: 'PREPARATION',
  number: getFirstFreeRoundNumber(rounds),
  startTime: '',
  endTime: '',
  votingEnabled: false,
  votingEndTime: '',
  trackUploadDeadline: '',
  votingSystem: '',
  criteriaPreset: '',
  previousRoundId: '',
  advancement: { type: 'NONE', threshold: null, pairWinnerMode: null },
  limitations: [],
  participants: [],
});

/** Пустая форма нового ограничения */
const emptyNewRoundLimitation = () => ({
  name: '',
  description: '',
  limitationType: 'TRACK_DURATION',
  minTrackDuration: null,
  maxTrackDuration: null,
  minTextLength: null,
  maxTextLength: null,
  forbiddenWords: '',
  maxFileSizeMb: null,
  allowedFormats: '',
  minAge: null,
  isActive: true,
});

/**
 * Валидация формы раунда перед отправкой.
 * @throws {Error} Текст первой найденной ошибки
 */
const validateRoundForm = (form) => {
  if (!form.title?.trim()) {
    throw new Error('Заполните название раунда');
  }
  if (!form.startTime || !form.endTime) {
    throw new Error('Заполните даты начала и окончания раунда');
  }
  if (new Date(form.startTime.replace(' ', 'T')) >= new Date(form.endTime.replace(' ', 'T'))) {
    throw new Error('Время начала раунда должно быть раньше времени окончания');
  }
  if (form.votingEnabled) {
    if (!form.votingEndTime) {
      throw new Error('Укажите время окончания голосования');
    }
    if (new Date(form.votingEndTime.replace(' ', 'T')) <= new Date(form.endTime.replace(' ', 'T'))) {
      throw new Error('Время окончания голосования должно быть позже окончания раунда');
    }
  }
  if (form.advancement?.type === 'VOTE_THRESHOLD' && (!form.advancement.threshold || form.advancement.threshold < 1)) {
    throw new Error('Укажите минимальное число голосов для прохождения (не менее 1)');
  }
  if (form.advancement?.type === 'TOP_N_BY_SCORE' && (!form.advancement.threshold || form.advancement.threshold < 1)) {
    throw new Error('Укажите число проходящих участников (не менее 1)');
  }
  if (form.advancement?.type === 'PAIR_WINNER' && !form.advancement.pairWinnerMode) {
    throw new Error('Выберите режим определения победителя пары');
  }
};

/**
 * Собрать payload RoundRequest из формы.
 * advancement передаётся только если тип не NONE; номер раунда —
 * null при создании (сервер присвоит первый свободный автоматически).
 */
const buildRoundPayload = (form, { includeStatus = true } = {}) => {
  const advancement = form.advancement?.type && form.advancement.type !== 'NONE'
    ? {
        type: form.advancement.type,
        threshold: form.advancement.threshold ?? null,
        pairWinnerMode:
          form.advancement.type === 'PAIR_WINNER' ? (form.advancement.pairWinnerMode || null) : null,
      }
    : null;

  return {
    title: form.title.trim(),
    description: form.description?.trim() || null,
    theme: form.theme?.trim() || null,
    task: form.task?.trim() || null,
    type: form.type,
    ...(includeStatus && { status: form.status || 'PREPARATION' }),
    number: form.number ? parseInt(form.number, 10) : null,
    startTime: form.startTime,
    endTime: form.endTime,
    votingEnabled: form.votingEnabled || false,
    votingEndTime: form.votingEnabled ? (form.votingEndTime || null) : null,
    trackUploadDeadline: form.trackUploadDeadline || null,
    votingSystem: form.votingSystem || undefined,
    criteriaPreset: form.criteriaPreset || undefined,
    previousRoundId: form.previousRoundId || null,
    advancement,
    // limitations всегда массивом — при редактировании пустой массив удаляет все ограничения
    limitations: (form.limitations || []).map((lim) => ({
      ...lim,
      forbiddenWords: lim.forbiddenWords?.trim() || null,
    })),
    participants: form.participants || [],
  };
};

/**
 * Хук для управления раундами баттла
 */
export const useBattleRounds = (battleId, fetchRounds) => {
  const [isRoundModalOpen, setIsRoundModalOpen] = useState(false);
  const [editingRoundId, setEditingRoundId] = useState(null);
  const [roundActionLoading, setRoundActionLoading] = useState({});
  const [expandedRoundDetails, setExpandedRoundDetails] = useState({});
  const [loadingRoundDetails, setLoadingRoundDetails] = useState({});

  const [roundForm, setRoundForm] = useState(emptyRoundForm());

  const [newRoundRule, setNewRoundRule] = useState({ title: '', description: '' });
  const [newRoundLimitation, setNewRoundLimitation] = useState(emptyNewRoundLimitation());

  /** Открыть модалку создания раунда */
  const openCreateRoundModal = useCallback((rounds = []) => {
    setEditingRoundId(null);
    setRoundForm(emptyRoundForm(rounds));
    setNewRoundLimitation(emptyNewRoundLimitation());
    setIsRoundModalOpen(true);
  }, []);

  /** Открыть модалку создания раунда из вкладки */
  const handleCreateRoundFromTab = useCallback(
    (tabId, rounds = []) => {
      const typeMap = {
        qualifying: 'QUALIFYING',
        draw: 'DRAW',
        table: 'PAIR',
      };

      setEditingRoundId(null);
      setRoundForm(emptyRoundForm(rounds, typeMap[tabId] || 'QUALIFYING'));
      setNewRoundLimitation(emptyNewRoundLimitation());
      setIsRoundModalOpen(true);
    },
    []
  );

  /** Открыть модалку редактирования раунда */
  const openEditRoundModal = useCallback((round) => {
    setEditingRoundId(round.id);
    setRoundForm({
      title: round.title || '',
      description: round.description || '',
      theme: round.theme || '',
      task: round.task || '',
      type: round.type || 'QUALIFYING',
      status: round.status || 'PREPARATION',
      number: round.number || 1,
      startTime: round.startTime
        ? round.startTime.replace('T', ' ').substring(0, 16)
        : '',
      endTime: round.endTime
        ? round.endTime.replace('T', ' ').substring(0, 16)
        : '',
      votingEnabled: round.votingEnabled || false,
      votingEndTime: round.votingEndTime
        ? round.votingEndTime.replace('T', ' ').substring(0, 16)
        : '',
      trackUploadDeadline: round.trackUploadDeadline
        ? round.trackUploadDeadline.replace('T', ' ').substring(0, 16)
        : '',
      votingSystem: round.votingSystem || '',
      criteriaPreset: round.criteriaPreset || '',
      previousRoundId: round.previousRoundId || '',
      advancement: {
        type: round.advancement?.type || 'NONE',
        threshold: round.advancement?.threshold ?? null,
        pairWinnerMode: round.advancement?.pairWinnerMode || null,
      },
      limitations: round.limitations || [],
      participants: (round.participants || []).map((p) => p.id),
    });
    setNewRoundLimitation(emptyNewRoundLimitation());
    setIsRoundModalOpen(true);
  }, []);

  /** Закрыть модалку */
  const closeRoundModal = useCallback(() => {
    setIsRoundModalOpen(false);
    setEditingRoundId(null);
  }, []);

  /** Изменение поля формы */
  const handleRoundFormChange = useCallback((field, value) => {
    setRoundForm((prev) => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Добавить всех подтверждённых участников баттла в созданный отборочный раунд.
   * Вызывается при создании QUALIFYING-раунда без явно выбранных участников.
   * Ошибка не блокирует создание раунда — только логируется.
   */
  const addAllApprovedParticipantsToRound = useCallback(
    async (roundId) => {
      try {
        const participantsData = await battlesApi.getParticipants(battleId);
        const allParticipants = Array.isArray(participantsData)
          ? participantsData
          : participantsData?.content || [];

        const approvedIds = allParticipants
          .filter(
            (p) =>
              p.role === 'PARTICIPANT' && p.applicationStatus === 'APPROVED'
          )
          .map((p) => p.id);

        if (approvedIds.length === 0) {
          console.log('[useBattleRounds] No approved participants to add to qualifying round');
          return;
        }

        await battlesApi.addRoundParticipants(battleId, roundId, approvedIds);
        console.log(
          `[useBattleRounds] Added ${approvedIds.length} approved participants to qualifying round`
        );
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to add approved participants to qualifying round:',
          err.message
        );
      }
    },
    [battleId]
  );

  /** Создание раунда */
  const handleCreateRound = useCallback(
    async (e) => {
      e.preventDefault();
      validateRoundForm(roundForm);
      try {
        setRoundActionLoading((prev) => ({ ...prev, create: true }));

        const payload = buildRoundPayload(roundForm, { includeStatus: false });

        console.log('[useBattleRounds] Creating round with payload:', payload);

        const createdRound = await battlesApi.createRound(battleId, payload);

        // При создании отборочного раунда без выбранных участников
        // автоматически добавляем всех подтверждённых участников баттла
        if (
          roundForm.type === 'QUALIFYING' &&
          (!roundForm.participants || roundForm.participants.length === 0) &&
          createdRound?.id
        ) {
          await addAllApprovedParticipantsToRound(createdRound.id);
        }

        await fetchRounds();
        closeRoundModal();
      } catch (err) {
        console.error('[useBattleRounds] Failed to create round:', err);
        console.error('[useBattleRounds] Error response:', err.response?.data);
        console.error('[useBattleRounds] Error status:', err.response?.status);
        // Пробрасываем ошибку дальше для отображения пользователю
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({ ...prev, create: false }));
      }
    },
    [battleId, roundForm, fetchRounds, closeRoundModal, addAllApprovedParticipantsToRound]
  );

  /** Обновление раунда */
  const handleUpdateRound = useCallback(
    async (e) => {
      e.preventDefault();
      if (!editingRoundId) return;
      validateRoundForm(roundForm);
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [editingRoundId]: true,
        }));

        const payload = buildRoundPayload(roundForm);

        console.log('[useBattleRounds] Updating round:', editingRoundId, 'with payload:', payload);

        await battlesApi.updateRound(battleId, editingRoundId, payload);
        await fetchRounds();
        closeRoundModal();
      } catch (err) {
        console.error('[useBattleRounds] Failed to update round:', err);
        console.error('[useBattleRounds] Error response:', err.response?.data);
        console.error('[useBattleRounds] Error status:', err.response?.status);
        // Пробрасываем ошибку дальше для отображения пользователю
        if (err.response?.data?.message) {
          throw new Error(err.response.data.message);
        }
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [editingRoundId]: false,
        }));
      }
    },
    [battleId, editingRoundId, roundForm, fetchRounds, closeRoundModal]
  );

  /** Удаление раунда */
  const handleDeleteRound = useCallback(
    async (roundId) => {
      if (!window.confirm('Вы уверены, что хотите удалить этот раунд?')) return;
      try {
        setRoundActionLoading((prev) => ({ ...prev, [roundId]: true }));
        await battlesApi.deleteRound(battleId, roundId);
        await fetchRounds();
      } catch (err) {
        console.error('[useBattleRounds] Failed to delete round:', err.message);
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({ ...prev, [roundId]: false }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Обновление статуса раунда */
  const handleUpdateRoundStatus = useCallback(
    async (roundId, newStatus) => {
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`status-${roundId}`]: true,
        }));
        await battlesApi.updateRoundStatus(battleId, roundId, newStatus);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to update round status:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`status-${roundId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Переключение деталей раунда */
  const toggleRoundDetails = useCallback(
    (roundId, fetchRoundRules, fetchRoundLimitations) => {
      console.log(`[useBattleRounds] toggleRoundDetails called for round ${roundId}`);
      console.log(`[useBattleRounds] fetchRoundRules:`, !!fetchRoundRules, `fetchRoundLimitations:`, !!fetchRoundLimitations);

      setExpandedRoundDetails((prev) => {
        const next = { ...prev, [roundId]: !prev[roundId] };
        console.log(`[useBattleRounds] Setting expandedRoundDetails:`, next);

        if (next[roundId]) {
          // Устанавливаем состояние загрузки
          setLoadingRoundDetails((prev) => ({ ...prev, [roundId]: true }));
          console.log(`[useBattleRounds] Setting loadingRoundDetails[${roundId}] = true`);

          // Загружаем данные
          if (fetchRoundRules) {
            console.log(`[useBattleRounds] Calling fetchRoundRules(${roundId})`);
            fetchRoundRules(roundId);
          }
          if (fetchRoundLimitations) {
            console.log(`[useBattleRounds] Calling fetchRoundLimitations(${roundId})`);
            fetchRoundLimitations(roundId);
          }

          // Снимаем состояние загрузки (с небольшой задержкой, чтобы данные успели загрузиться)
          setTimeout(() => {
            setLoadingRoundDetails((prev) => ({ ...prev, [roundId]: false }));
            console.log(`[useBattleRounds] Setting loadingRoundDetails[${roundId}] = false`);
          }, 300);
        }
        return next;
      });
    },
    []
  );

  /** Добавить правило раунда */
  const handleAddRoundRule = useCallback(
    async (roundId) => {
      if (!newRoundRule.title.trim()) {
        throw new Error('Введите название правила');
      }
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-rule-${roundId}`]: true,
        }));
        await battlesApi.createRoundRule(battleId, roundId, {
          title: newRoundRule.title.trim(),
          description: newRoundRule.description.trim() || null,
        });
        await fetchRounds();
        setNewRoundRule({ title: '', description: '' });
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to add round rule:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-rule-${roundId}`]: false,
        }));
      }
    },
    [battleId, newRoundRule, fetchRounds]
  );

  /** Удалить правило раунда */
  const handleDeleteRoundRule = useCallback(
    async (roundId, ruleId) => {
      if (!window.confirm('Удалить это правило?')) return;
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`del-rule-${ruleId}`]: true,
        }));
        await battlesApi.deleteRoundRule(battleId, roundId, ruleId);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to delete round rule:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`del-rule-${ruleId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Изменение поля формы нового ограничения */
  const handleNewLimitationChange = useCallback((field, value) => {
    setNewRoundLimitation((prev) => ({ ...prev, [field]: value }));
  }, []);

  /** Добавить ограничение в форму */
  const handleAddLimitationToForm = useCallback(() => {
    if (!newRoundLimitation.name.trim()) {
      throw new Error('Введите название ограничения');
    }

    setRoundForm((prev) => ({
      ...prev,
      limitations: [
        ...prev.limitations,
        {
          name: newRoundLimitation.name.trim(),
          description: newRoundLimitation.description?.trim() || null,
          limitationType: newRoundLimitation.limitationType,
          minTrackDuration: newRoundLimitation.minTrackDuration,
          maxTrackDuration: newRoundLimitation.maxTrackDuration,
          minTextLength: newRoundLimitation.minTextLength,
          maxTextLength: newRoundLimitation.maxTextLength,
          forbiddenWords: newRoundLimitation.forbiddenWords?.trim() || null,
          maxFileSizeMb: newRoundLimitation.maxFileSizeMb,
          allowedFormats: newRoundLimitation.allowedFormats?.trim() || null,
          minAge: newRoundLimitation.minAge,
          isActive: true,
        },
      ],
    }));
    // Сброс формы нового ограничения
    setNewRoundLimitation(emptyNewRoundLimitation());
  }, [newRoundLimitation]);

  /** Удалить ограничение из формы */
  const handleRemoveLimitationFromForm = useCallback((index) => {
    setRoundForm((prev) => ({
      ...prev,
      limitations: prev.limitations.filter((_, i) => i !== index),
    }));
  }, []);

  /** Добавить участника в раунд */
  const handleAddRoundParticipant = useCallback(
    async (roundId, personId) => {
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-participant-${roundId}-${personId}`]: true,
        }));
        // API принимает массив participantIds
        await battlesApi.addRoundParticipants(battleId, roundId, [personId]);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to add round participant:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-participant-${roundId}-${personId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Добавить жюри в раунд */
  const handleAddRoundJudges = useCallback(
    async (roundId, judgeIds) => {
      if (!Array.isArray(judgeIds) || judgeIds.length === 0) return;
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-judges-${roundId}`]: true,
        }));
        await battlesApi.addRoundJudges(battleId, roundId, judgeIds);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to add judges to round:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`add-judges-${roundId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  /** Удалить участника из раунда */
  const handleRemoveRoundParticipant = useCallback(
    async (roundId, personId) => {
      if (!window.confirm('Удалить участника из раунда?')) return;
      try {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`remove-participant-${roundId}-${personId}`]: true,
        }));
        await battlesApi.removeRoundParticipant(battleId, roundId, personId);
        await fetchRounds();
      } catch (err) {
        console.error(
          '[useBattleRounds] Failed to remove round participant:',
          err.message
        );
        throw err;
      } finally {
        setRoundActionLoading((prev) => ({
          ...prev,
          [`remove-participant-${roundId}-${personId}`]: false,
        }));
      }
    },
    [battleId, fetchRounds]
  );

  return {
    // State
    isRoundModalOpen,
    editingRoundId,
    roundActionLoading,
    expandedRoundDetails,
    loadingRoundDetails,
    roundForm,
    newRoundRule,
    newRoundLimitation,

    // Setters
    setRoundForm,
    setNewRoundRule,
    handleNewLimitationChange,

    // Actions
    openCreateRoundModal,
    handleCreateRoundFromTab,
    openEditRoundModal,
    closeRoundModal,
    handleRoundFormChange,
    handleCreateRound,
    handleUpdateRound,
    handleDeleteRound,
    handleUpdateRoundStatus,
    toggleRoundDetails,
    handleAddRoundRule,
    handleDeleteRoundRule,
    handleAddLimitationToForm,
    handleRemoveLimitationFromForm,
    handleAddRoundParticipant,
    handleRemoveRoundParticipant,
    handleAddRoundJudges,
  };
};
